import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import { Seo } from "@/components/Seo";
import { TYPE_META } from "@/pages/Community";
import { ReportButton } from "@/components/ReportButton";
import ReactMarkdown from "react-markdown";
import { ArrowLeft } from "lucide-react";

function embedSrc(url) {
  if (!url) return null;
  if (url.includes("youtube.com/watch")) return url.replace("watch?v=", "embed/");
  if (url.includes("youtu.be/")) return "https://www.youtube.com/embed/" + url.split("youtu.be/")[1];
  if (url.includes("open.spotify.com/") && !url.includes("/embed/")) return url.replace("open.spotify.com/", "open.spotify.com/embed/");
  return url;
}

export default function CommunityPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.contentGet(id).then(setPost).catch(() => setMissing(true));
  }, [id]);

  if (missing) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">This piece is no longer available. <Link to="/community" className="text-forest underline">Back to Community</Link></div>;
  if (!post) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;

  const meta = TYPE_META[post.content_type] || TYPE_META.article;
  const embed = embedSrc(post.embed_url);

  return (
    <div className="bg-cream" data-testid="community-post-page">
      <Seo title={post.title} description={post.body.slice(0, 150)} />
      <article className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/community" className="text-sm text-neutral-500 hover:text-forest inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Community
        </Link>
        <Badge className="mt-6 rounded-full text-[10px] uppercase tracking-wide bg-terracotta/10 text-terracotta border-none">
          <meta.icon className="w-3 h-3 mr-1" />{meta.label}
        </Badge>
        <h1 className="font-display text-4xl sm:text-5xl text-forest mt-4 tracking-tight leading-tight">{post.title}</h1>
        <div className="text-sm text-neutral-500 mt-4 flex items-center gap-4">
          <span>{post.author_name} · {new Date(post.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</span>
          <ReportButton targetType="content" targetId={post.id} />
        </div>

        {post.cover_image && (
          <img src={imgSrc(post.cover_image)} alt={post.title} className="w-full h-80 object-cover rounded-3xl border border-forest/10 mt-8" />
        )}

        {embed && (
          <div className="mt-8 rounded-3xl overflow-hidden border border-forest/10">
            <iframe
              data-testid="community-embed"
              src={embed}
              title={post.title}
              className="w-full aspect-video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        <div className="mt-8" data-testid="post-body">
          <ReactMarkdown
            components={{
              p: ({ node, ...props }) => <p className="text-neutral-700 leading-relaxed text-lg mb-5" {...props} />,
              ul: ({ node, ...props }) => <ul className="mb-5 space-y-1.5" {...props} />,
              ol: ({ node, ...props }) => <ol className="mb-5 space-y-1.5 list-decimal ml-5" {...props} />,
              li: ({ node, ...props }) => <li className="text-neutral-700 leading-relaxed text-lg ml-5 list-disc" {...props} />,
              strong: ({ node, ...props }) => <strong className="font-semibold text-forest" {...props} />,
              h1: ({ node, ...props }) => <h2 className="font-display text-2xl text-forest mt-8 mb-3" {...props} />,
              h2: ({ node, ...props }) => <h2 className="font-display text-2xl text-forest mt-8 mb-3" {...props} />,
              h3: ({ node, ...props }) => <h3 className="font-display text-xl text-forest mt-6 mb-2" {...props} />,
              img: ({ node, src, alt, ...props }) => <img src={imgSrc(src)} alt={alt || ""} className="rounded-2xl my-6 w-full max-h-[420px] object-cover border border-forest/10" {...props} />,
              a: ({ node, ...props }) => <a className="text-forest underline" target="_blank" rel="noreferrer" {...props} />,
              blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-terracotta/40 pl-4 italic text-neutral-600 my-5" {...props} />,
            }}
          >
            {post.body}
          </ReactMarkdown>
        </div>
      </article>
    </div>
  );
}
